import { useMemo, useRef, useState } from "react";
import Icon from "./Icon";
import { menuUrl } from "../../menu.js";
import {
    FIELDS, IMPORT_MAX_BYTES, IMPORT_MAX_ROWS, MAX_CATEGORIES, SKIP,
    buildRows, checkRows, decodeText, detectMapping, headersKey, looksLikeTemplate,
    mappingIsComplete, parseCsv, parsePrice, planImport, splitTable
} from "../../importCatalog.js";
import { AI_MAPPING, AI_MAX_PHOTOS, checkSelection, isPdf, prepareFiles, readWithAI, toTable } from "../../aiImport.js";
import { auth } from "../../firebase/config.js";
import "../../panel-import.css";

export const TEMPLATE_URL = "/plantilla-productos-miqrapp.xlsx";
const STEPS = ["Archivo", "Columnas", "Revisar", "Importar"];
const PAGE = 25;
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// Cómo se mapeó cada archivo, para no preguntar de nuevo si se sube otro con las mismas columnas.
const MAPPINGS_KEY = "miqrapp-importar-columnas";
const savedMapping = (headers) => {
    try {
        const all = JSON.parse(window.localStorage.getItem(MAPPINGS_KEY) || "{}");
        const m = all[headersKey(headers)];
        return Array.isArray(m) && m.length === headers.length ? m : null;
    } catch (error) {
        return null;
    }
}
const saveMapping = (headers, mapping) => {
    try {
        const all = JSON.parse(window.localStorage.getItem(MAPPINGS_KEY) || "{}");
        all[headersKey(headers)] = mapping;
        window.localStorage.setItem(MAPPINGS_KEY, JSON.stringify(all));
    } catch (error) {
        console.error(error);
    }
}

const readFile = async (file) => {
    const name = file.name.toLowerCase();
    if (file.size > IMPORT_MAX_BYTES) throw new Error("El archivo pesa más de 5 MB. Probá sacándole hojas o imágenes.");
    if (name.endsWith(".xls")) throw new Error("Ese es el formato viejo de Excel (.xls). Abrilo y guardalo como .xlsx o CSV.");
    const buffer = await file.arrayBuffer();
    if (name.endsWith(".xlsx")) {
        try {
            const { readXlsx } = await import("../../xlsx.js");
            return await readXlsx(buffer);
        } catch (error) {
            console.error(error);
            throw new Error("No pudimos leer el Excel. Probá guardarlo de nuevo como .xlsx o como CSV.");
        }
    }
    if (name.endsWith(".csv") || name.endsWith(".txt") || name.endsWith(".tsv")) return parseCsv(decodeText(buffer));
    throw new Error("Subí un archivo .xlsx o .csv.");
}

const Steps = ({ current, skipped }) => (
    <ol className="lp-imp-steps" aria-label="Pasos">
        {STEPS.map((s, i) => (
            <li key={s} className={(i < current ? "is-done" : i === current ? "is-on" : "") + (skipped && i === 1 ? " is-skip" : "")} aria-current={i === current ? "step" : undefined}>
                <span><b>{i + 1}.</b> {s}</span>
            </li>
        ))}
    </ol>
);

const fileKind = (name) => /\.pdf$/i.test(name) ? "PDF" : /\.(csv|txt|tsv)$/i.test(name) ? "CSV" : /\.xlsx?$/i.test(name) ? "XLS" : "FOTO";

const FileChip = ({ file, meta, onRemove }) => (
    <div className="lp-imp-file">
        <span className={`lp-imp-sheet is-${fileKind(file).toLowerCase()}`}>{fileKind(file)}</span>
        <div><strong>{file}</strong><span className="lp-panel-muted">{meta}</span></div>
        {onRemove && <button type="button" className="lp-panel-icon-btn" aria-label="Elegir otro archivo" onClick={onRemove}><Icon name="close" size={18}></Icon></button>}
    </div>
);

const sizeLabel = (bytes) => bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1).replace(".", ",")} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

// Mientras la IA lee la carta.
const AiReading = ({ files, onCancel }) => (
    <section className="lp-panel-card lp-panel-stack lp-imp-gap lp-imp-ai" aria-live="polite">
        <div className="lp-imp-files">
            {files.map((f) => <FileChip key={f.name + f.size} file={f.name} meta={sizeLabel(f.size)}></FileChip>)}
        </div>
        <div className="lp-imp-ai-box">
            <span className="lp-imp-ai-tag"><Icon name="star" size={14} strokeWidth={2.4}></Icon> Leyendo con IA</span>
            <h2>Estamos leyendo tu carta...</h2>
            <p className="lp-panel-muted">Suele tardar entre 10 segundos y un minuto, según el largo de la carta. No cierres esta pantalla.</p>
            <div className="lp-imp-ai-bar" role="progressbar" aria-label="Leyendo la carta"><span></span></div>
            <p className="lp-imp-note is-info"><Icon name="alert" size={18} strokeWidth={2.6}></Icon><span>La IA puede equivocarse con precios o nombres. En el paso <b>Revisar</b> vas a poder corregir todo antes de que se publique.</span></p>
            <button type="button" className="lp-btn lp-btn-secondary lp-panel-btn-sm lp-imp-center" onClick={onCancel}>Cancelar</button>
        </div>
    </section>
);

// Paso 1: elegir el archivo.
const PickFile = ({ onLoaded, initialSource = "excel" }) => {
    const input = useRef(null);
    const aborter = useRef(null);
    const [source, setSource] = useState(initialSource);
    const [over, setOver] = useState(false);
    const [reading, setReading] = useState(false);
    const [error, setError] = useState("");
    const [photos, setPhotos] = useState([]);
    const [aiBusy, setAiBusy] = useState(false);
    const ai = source === "ia";

    const handle = async (file) => {
        if (!file) return;
        setError("");
        setReading(true);
        try {
            const table = splitTable(await readFile(file));
            if (!table.rows.length) throw new Error("El archivo no tiene productos. Fijate que la primera hoja tenga los encabezados y debajo una fila por producto.");
            if (table.rows.length > IMPORT_MAX_ROWS) throw new Error(`El archivo tiene ${table.rows.length} filas. Podés importar hasta ${IMPORT_MAX_ROWS} por vez: dividilo en partes.`);
            onLoaded(file.name, table);
        } catch (e) {
            setError(e.message);
            setReading(false);
        }
        if (input.current) input.current.value = "";
    }

    // Con IA se pueden sumar varias fotos antes de mandarlas.
    const addAiFiles = (list) => {
        const picked = Array.from(list || []);
        if (input.current) input.current.value = "";
        if (!picked.length) return;
        const next = picked.some(isPdf) ? picked : [...photos.filter((f) => !isPdf(f)), ...picked];
        const problem = checkSelection(next);
        setError(problem);
        if (!problem) setPhotos(next);
    }

    const readAi = async () => {
        const problem = checkSelection(photos);
        if (problem) { setError(problem); return; }
        setError("");
        setAiBusy(true);
        const controller = new AbortController();
        aborter.current = controller;
        try {
            const files = await prepareFiles(photos);
            const idToken = await auth.currentUser.getIdToken();
            const { productos, incomplete } = await readWithAI({ idToken, files, signal: controller.signal });
            const table = toTable(productos);
            if (!table.rows.length) throw new Error("No encontramos productos en ese archivo. Probá con una foto más nítida, de frente y con buena luz.");
            if (table.rows.length > IMPORT_MAX_ROWS) table.rows = table.rows.slice(0, IMPORT_MAX_ROWS);
            const name = photos.length === 1 ? photos[0].name : `${photos.length} fotos de la carta`;
            onLoaded(name, table, { ai: true, incomplete });
        } catch (e) {
            if (e.name !== "AbortError") setError(e.message);
            setAiBusy(false);
        }
        aborter.current = null;
    }

    const cancelAi = () => {
        aborter.current?.abort();
        setAiBusy(false);
    }

    const choose = (s) => {
        if (s === source) return;
        setSource(s);
        setError("");
        setPhotos([]);
    }

    if (aiBusy) return <AiReading files={photos} onCancel={cancelAi}></AiReading>;

    const onSourceKey = (e) => {
        if (["ArrowRight", "ArrowDown", "ArrowLeft", "ArrowUp"].includes(e.key)) {
            e.preventDefault();
            choose(ai ? "excel" : "ia");
        }
    }

  return (
    <div className="lp-imp-layout">
        <section className="lp-panel-card lp-panel-stack lp-imp-gap">
            <h2>¿De dónde sacamos tus productos?</h2>
            <div className="lp-imp-sources" role="radiogroup" aria-label="Tipo de archivo" onKeyDown={onSourceKey}>
                <button type="button" className={"lp-imp-source" + (ai ? "" : " is-on")} role="radio" aria-checked={!ai} tabIndex={ai ? -1 : 0} onClick={() => choose("excel")}>
                    <span className="lp-imp-radio" aria-hidden="true"></span>
                    <span className="lp-imp-ic" aria-hidden="true"><Icon name="grid" size={22}></Icon></span>
                    <h3>Excel o CSV</h3>
                    <p className="lp-panel-muted">Una planilla con nombre, precio y categoría. Lo más rápido y exacto.</p>
                </button>
                <button type="button" className={"lp-imp-source" + (ai ? " is-on" : "")} role="radio" aria-checked={ai} tabIndex={ai ? 0 : -1} onClick={() => choose("ia")}>
                    <span className="lp-imp-radio" aria-hidden="true"></span>
                    <span className="lp-imp-ic lp-imp-ic-ai" aria-hidden="true"><Icon name="star" size={22}></Icon></span>
                    <h3>PDF o foto de tu carta</h3>
                    <p className="lp-panel-muted">La leemos con inteligencia artificial y revisás todo antes de publicar.</p>
                </button>
            </div>
            <label
                className={over ? "lp-imp-drop is-over" : "lp-imp-drop"}
                onDragOver={(e) => { e.preventDefault(); setOver(true); }}
                onDragLeave={() => setOver(false)}
                onDrop={(e) => { e.preventDefault(); setOver(false); ai ? addAiFiles(e.dataTransfer.files) : handle(e.dataTransfer.files?.[0]); }}
            >
                {ai
                    ? <input key="ia" ref={input} type="file" multiple className="lp-panel-sr" accept=".pdf,application/pdf,image/*" onChange={(e) => addAiFiles(e.target.files)}/>
                    : <input key="excel" ref={input} type="file" className="lp-panel-sr" accept=".xlsx,.csv,.txt,.tsv,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv" onChange={(e) => handle(e.target.files?.[0])}/>}
                <span className={"lp-imp-drop-ic" + (ai ? " is-ai" : "")} aria-hidden="true"><Icon name={ai ? "camera" : "up"} size={24} strokeWidth={2.2}></Icon></span>
                {ai
                    ? <>
                        <strong>{photos.length ? "Sumar más fotos" : "Arrastrá el PDF o las fotos acá"}</strong>
                        <span className="lp-panel-muted">o tocá para elegir · un PDF de hasta 8 MB o hasta {AI_MAX_PHOTOS} fotos</span>
                        <span className="lp-btn lp-btn-secondary lp-panel-btn-sm" aria-hidden="true">{photos.length ? "Agregar fotos" : "Elegir archivos"}</span>
                    </>
                    : <>
                        <strong>{reading ? "Leyendo el archivo..." : "Arrastrá tu archivo acá"}</strong>
                        <span className="lp-panel-muted">o tocá para elegirlo · .xlsx o .csv · hasta 5 MB</span>
                        <span className="lp-btn lp-btn-primary lp-panel-btn-sm" aria-hidden="true">Elegir archivo</span>
                    </>}
            </label>
            {ai && photos.length > 0 &&
                <div className="lp-imp-files">
                    {photos.map((f, i) => (
                        <FileChip key={f.name + f.size + i} file={f.name} meta={sizeLabel(f.size)}
                            onRemove={() => { setPhotos((list) => list.filter((_, j) => j !== i)); setError(""); }}></FileChip>
                    ))}
                </div>
            }
            {error && <p className="lp-imp-note is-err" role="alert"><Icon name="alert" size={18} strokeWidth={2.6}></Icon><span>{error}</span></p>}
            {ai && photos.length > 0 &&
                <button type="button" className="lp-btn lp-btn-primary lp-imp-center lp-imp-ai-go" onClick={readAi}>
                    <Icon name="star" size={18} strokeWidth={2.4}></Icon> Leer con IA
                </button>
            }
        </section>
        <div className="lp-panel-stack lp-imp-gap">
            {ai
                ? <section className="lp-panel-card lp-panel-stack">
                    <h2 className="lp-imp-h3">Para que la IA lea bien</h2>
                    <ul className="lp-imp-tips">
                        <li><Icon name="check" size={18} strokeWidth={2.4}></Icon><span>Sacá la foto de frente, con buena luz y sin reflejos.</span></li>
                        <li><Icon name="check" size={18} strokeWidth={2.4}></Icon><span>Si la carta tiene varias páginas, subí una foto por página.</span></li>
                        <li><Icon name="check" size={18} strokeWidth={2.4}></Icon><span>Si un producto tiene varios tamaños, lo separamos en uno por tamaño.</span></li>
                        <li><Icon name="check" size={18} strokeWidth={2.4}></Icon><span>Antes de guardar vas a ver todo y corregir lo que haga falta.</span></li>
                        <li className="is-muted"><Icon name="check" size={18} strokeWidth={2.4}></Icon><span>Las fotos de los productos no se importan: las agregás después.</span></li>
                    </ul>
                </section>
                : <>
                    <section className="lp-panel-card lp-panel-stack">
                        <div className="lp-imp-template">
                            <span className="lp-imp-sheet">XLS</span>
                            <div><strong>Plantilla de MiQRapp</strong><p className="lp-panel-muted">Completala y subila tal cual.</p></div>
                        </div>
                        <div className="lp-imp-cols">
                            <code className="is-req">Nombre</code><code className="is-req">Precio</code><code className="is-req">Categoría</code>
                            <code>Descripción</code><code>Etiquetas</code><code>Agotado</code>
                        </div>
                        <a className="lp-btn lp-btn-secondary lp-panel-btn-sm lp-imp-center" href={TEMPLATE_URL} download>
                            <Icon name="download" size={18}></Icon> Descargar plantilla
                        </a>
                    </section>
                    <section className="lp-panel-card lp-panel-stack">
                        <h2 className="lp-imp-h3">Antes de subir</h2>
                        <ul className="lp-imp-tips">
                            <li><Icon name="check" size={18} strokeWidth={2.4}></Icon><span>Si tu Excel tiene otras columnas, no pasa nada: en el paso siguiente nos decís cuál es cuál.</span></li>
                            <li><Icon name="check" size={18} strokeWidth={2.4}></Icon><span>Los precios pueden ir con o sin $ y puntos.</span></li>
                            <li><Icon name="check" size={18} strokeWidth={2.4}></Icon><span>Antes de guardar vas a ver todo y corregir lo que haga falta.</span></li>
                            <li><Icon name="check" size={18} strokeWidth={2.4}></Icon><span>Las fotos las agregás después, desde cada producto.</span></li>
                        </ul>
                    </section>
                </>}
        </div>
    </div>
  )
}

// Paso 2: a qué campo va cada columna.
const MapColumns = ({ fileName, table, mapping, setMapping, onBack, onNext }) => {
    const examples = (i) => table.rows.map((r) => r[i]).filter(Boolean).slice(0, 3).join(", ");
    const missing = FIELDS.filter((f) => f.required && !mapping.includes(f.id));
    const choose = (i, value) => setMapping((m) => m.map((x, j) => j === i ? value : (value !== SKIP && x === value ? SKIP : x)));

  return (
    <div className="lp-imp-layout">
        <section className="lp-panel-card lp-panel-stack lp-imp-gap">
            <FileChip file={fileName} meta={`${plural(table.rows.length, "fila", "filas")} · ${plural(table.headers.length, "columna", "columnas")}`}></FileChip>
            <div className="lp-imp-map">
                <div className="lp-imp-map-row lp-imp-map-head" aria-hidden="true"><span>Columna de tu archivo</span><span></span><span>Va a MiQRapp como</span></div>
                {table.headers.map((h, i) => (
                    <div className="lp-imp-map-row" key={i}>
                        <div className="lp-imp-map-src"><strong>{h}</strong><span>{examples(i) || "Vacía"}</span></div>
                        <span className="lp-imp-map-arrow" aria-hidden="true"><Icon name="arrow"></Icon></span>
                        <div className="lp-field">
                            <label htmlFor={"map-" + i} className="lp-panel-sr">{h} va a MiQRapp como</label>
                            <select id={"map-" + i} className={mapping[i] === SKIP ? "lp-imp-select is-skip" : "lp-imp-select is-ok"} value={mapping[i]} onChange={(e) => choose(i, e.target.value)}>
                                <option value={SKIP}>No importar</option>
                                {FIELDS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
                            </select>
                        </div>
                    </div>
                ))}
            </div>
            {missing.length > 0 && <p className="lp-imp-note is-err" role="alert"><Icon name="alert" size={18} strokeWidth={2.6}></Icon><span>Falta elegir qué columna es: <b>{missing.map((f) => f.label).join(", ")}</b>.</span></p>}
            <div className="lp-imp-footer">
                <button type="button" className="lp-btn lp-btn-secondary" onClick={onBack}>Atrás</button>
                <button type="button" className="lp-btn lp-btn-primary" disabled={missing.length > 0} onClick={onNext}>Seguir <Icon name="arrow" size={18}></Icon></button>
            </div>
        </section>
        <div className="lp-panel-stack lp-imp-gap">
            <section className="lp-panel-card lp-panel-stack">
                <h2 className="lp-imp-h3">Campos de MiQRapp</h2>
                <ul className="lp-imp-tips">
                    {FIELDS.filter((f) => f.id !== "disponible").map((f) => {
                        const on = mapping.includes(f.id) || (f.id === "agotado" && mapping.includes("disponible"));
                        return <li key={f.id} className={on ? "" : "is-muted"}><Icon name={on ? "check" : "minus"} size={18} strokeWidth={2.4}></Icon><span><strong>{f.id === "agotado" ? "Agotado" : f.label}</strong> · {f.required ? "obligatorio" : "opcional"}</span></li>;
                    })}
                </ul>
            </section>
            <p className="lp-imp-note is-info"><Icon name="alert" size={18} strokeWidth={2.4}></Icon><span>La próxima vez que subas un archivo con estas mismas columnas, nos acordamos y salteamos este paso.</span></p>
        </div>
    </div>
  )
}

const FILTERS = [
    ["todos", "Todos"], ["listos", "Listos"], ["errores", "Con errores"], ["existen", "Ya existen"], ["nuevas", "Categoría nueva"], ["sindesc", "Sin descripción"], ["sacados", "No se importan"]
];

// Sin descripción en el archivo y sin una anterior que se pueda conservar.
const noDescription = (r) => !r.description.trim() && !r.existing?.description;

// Paso 3: revisar y corregir.
const Review = ({ rows, setRows, products, categories, onBack, onNext }) => {
    const [filter, setFilter] = useState("todos");
    const [q, setQ] = useState("");
    const [page, setPage] = useState(0);
    const checked = useMemo(() => checkRows(rows, products, categories), [rows, products, categories]);

    const active = checked.filter((r) => !r.skipped);
    const counts = {
        todos: checked.length,
        listos: active.filter((r) => !r.hasErrors).length,
        errores: active.filter((r) => r.hasErrors).length,
        existen: active.filter((r) => r.existing).length,
        nuevas: active.filter((r) => r.newCategory).length,
        sindesc: active.filter(noDescription).length,
        sacados: checked.filter((r) => r.skipped).length
    };
    const matches = {
        todos: () => true, listos: (r) => !r.skipped && !r.hasErrors, errores: (r) => !r.skipped && r.hasErrors,
        existen: (r) => !r.skipped && r.existing, nuevas: (r) => !r.skipped && r.newCategory,
        sindesc: (r) => !r.skipped && noDescription(r), sacados: (r) => r.skipped
    };
    const term = q.trim().toLowerCase();
    const visible = checked.filter((r) => matches[filter](r) && (!term || r.name.toLowerCase().includes(term) || r.category.toLowerCase().includes(term)));
    const pages = Math.max(1, Math.ceil(visible.length / PAGE));
    const shown = visible.slice(Math.min(page, pages - 1) * PAGE, Math.min(page, pages - 1) * PAGE + PAGE);

    const edit = (key, field, value) => setRows((list) => list.map((r) => r.key === key ? { ...r, [field]: value } : r));
    const skipErrors = () => setRows((list) => list.map((r) => checked.find((c) => c.key === r.key)?.hasErrors ? { ...r, skipped: true } : r));
    const pick = (f) => { setFilter(f); setPage(0); };

  return (
    <section className="lp-panel-card lp-panel-stack lp-imp-gap">
        <div className="lp-imp-review-head">
            <div className="lp-imp-pills" role="group" aria-label="Filtrar filas">
                {FILTERS.filter(([id]) => id === "todos" || counts[id] > 0).map(([id, label]) => (
                    <button key={id} type="button" className={`lp-imp-pill is-${id}` + (filter === id ? " is-sel" : "")} aria-pressed={filter === id} onClick={() => pick(id)}>
                        {id === "errores" && <Icon name="alert" size={14} strokeWidth={3}></Icon>}
                        {id === "listos" && <Icon name="check" size={14} strokeWidth={2.6}></Icon>}
                        {label} {counts[id]}
                    </button>
                ))}
            </div>
            <div className="lp-panel-search lp-imp-search">
                <Icon name="search"></Icon>
                <label htmlFor="imp-buscar" className="lp-panel-sr">Buscar en el archivo</label>
                <input id="imp-buscar" type="search" placeholder="Buscar en el archivo..." value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }}/>
            </div>
        </div>

        <div className="lp-imp-table" role="table" aria-label="Productos del archivo">
            <div className="lp-imp-tr lp-imp-th" role="row">
                <span role="columnheader">#</span><span role="columnheader">Nombre</span><span role="columnheader">Categoría</span>
                <span role="columnheader" className="lp-imp-right">Precio</span><span role="columnheader">Descripción</span><span role="columnheader"><span className="lp-panel-sr">Acciones</span></span>
            </div>
            {shown.map((r) => {
                const price = parsePrice(r.price);
                return (
                    <div key={r.key} role="row" className={"lp-imp-tr" + (r.skipped ? " is-off" : r.hasErrors ? " is-err" : "")}>
                        <span role="cell" className="lp-imp-line">{r.line}</span>
                        <span role="cell" className="lp-imp-c-name">
                            <input aria-label={`Nombre, fila ${r.line}`} className={r.errors.name ? "lp-imp-cell is-bad" : "lp-imp-cell"} value={r.name} disabled={r.skipped} placeholder="Falta el nombre" onChange={(e) => edit(r.key, "name", e.target.value)}/>
                            {!r.skipped && r.errors.name && <small className="lp-imp-msg is-err"><Icon name="alert" size={14} strokeWidth={3}></Icon> {r.errors.name}</small>}
                            {!r.skipped && !r.errors.name && r.existing && <small className="lp-imp-msg is-warn"><Icon name="refresh" size={14} strokeWidth={2.4}></Icon> Ya existe en tu menú</small>}
                        </span>
                        <span role="cell" className="lp-imp-c-cat">
                            <input aria-label={`Categoría, fila ${r.line}`} className={r.errors.category ? "lp-imp-cell is-bad" : "lp-imp-cell"} value={r.category} disabled={r.skipped} placeholder="Falta la categoría" onChange={(e) => edit(r.key, "category", e.target.value)}/>
                            {!r.skipped && r.errors.category && <small className="lp-imp-msg is-err"><Icon name="alert" size={14} strokeWidth={3}></Icon> {r.errors.category}</small>}
                            {!r.skipped && !r.errors.category && r.newCategory && <small className="lp-imp-tag">Categoría nueva</small>}
                        </span>
                        <span role="cell" className="lp-imp-c-price">
                            <input aria-label={`Precio, fila ${r.line}`} inputMode="decimal" className={r.errors.price ? "lp-imp-cell lp-imp-right is-bad" : "lp-imp-cell lp-imp-right"} value={r.price} disabled={r.skipped} placeholder="Precio" onChange={(e) => edit(r.key, "price", e.target.value)}/>
                            {!r.skipped && r.errors.price && <small className="lp-imp-msg is-err"><Icon name="alert" size={14} strokeWidth={3}></Icon> {r.errors.price}</small>}
                            {!r.skipped && !r.errors.price && price !== r.price && <small className="lp-imp-msg lp-imp-right">Se guarda como ${Number(price).toLocaleString("es-AR")}</small>}
                            {!r.skipped && !r.errors.price && r.existing && <small className="lp-imp-msg">Hoy: ${Number(r.existing.price).toLocaleString("es-AR")}</small>}
                        </span>
                        <span role="cell" className="lp-imp-c-desc">
                            <input aria-label={`Descripción, fila ${r.line}`} className={"lp-imp-cell is-muted" + (r.errors.description ? " is-bad" : !r.skipped && noDescription(r) ? " is-empty" : "")} value={r.description} disabled={r.skipped} placeholder={r.existing?.description ? "Sin descripción en el archivo" : "Sin descripción. Tocá para agregar una"} onChange={(e) => edit(r.key, "description", e.target.value)}/>
                            {!r.skipped && r.errors.description && <small className="lp-imp-msg is-err">{r.errors.description}</small>}
                            {!r.skipped && !r.description.trim() && r.existing?.description && <small className="lp-imp-msg">Si sumás, queda la descripción que ya tiene</small>}
                        </span>
                        <span role="cell" className="lp-imp-c-act">
                            {r.skipped ?
                                <button type="button" className="lp-panel-icon-btn" aria-label={`Volver a incluir la fila ${r.line}`} title="Volver a incluir" onClick={() => edit(r.key, "skipped", false)}><Icon name="refresh" size={18}></Icon></button> :
                                <button type="button" className="lp-panel-icon-btn lp-panel-icon-danger" aria-label={`No importar la fila ${r.line}`} title="No importar esta fila" onClick={() => edit(r.key, "skipped", true)}><Icon name="trash" size={18}></Icon></button>
                            }
                        </span>
                    </div>
                );
            })}
            {!shown.length && <p className="lp-panel-empty">No hay filas que coincidan.</p>}
        </div>

        {pages > 1 &&
            <div className="lp-imp-pager">
                <span className="lp-panel-muted">Mostrando {shown.length} de {visible.length}</span>
                <div>
                    <button type="button" className="lp-panel-chip" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Anterior</button>
                    <span className="lp-panel-muted">Página {Math.min(page, pages - 1) + 1} de {pages}</span>
                    <button type="button" className="lp-panel-chip" disabled={page >= pages - 1} onClick={() => setPage((p) => p + 1)}>Siguiente</button>
                </div>
            </div>
        }

        {counts.errores > 0 && <p className="lp-imp-note is-err"><Icon name="alert" size={18} strokeWidth={2.6}></Icon><span><b>Corregí {counts.errores === 1 ? "la fila con error" : `las ${counts.errores} filas con errores`} o sacala{counts.errores === 1 ? "" : "s"}</b> para poder seguir. Las demás ya están bien.{filter !== "errores" && <> <button type="button" className="lp-text-link" onClick={() => pick("errores")}>Ver solo las filas con errores</button></>}</span></p>}
        {counts.errores === 0 && counts.listos === 0 && <p className="lp-imp-note is-err"><Icon name="alert" size={18} strokeWidth={2.6}></Icon><span>No quedó ningún producto para importar.</span></p>}
        <div className="lp-imp-footer">
            <button type="button" className="lp-btn lp-btn-secondary" onClick={onBack}>Atrás</button>
            <div className="lp-imp-footer-r">
                {counts.errores > 0 && <button type="button" className="lp-btn lp-btn-secondary" onClick={skipErrors}>Sacar filas con error</button>}
                <button type="button" className="lp-btn lp-btn-primary" disabled={counts.errores > 0 || counts.listos === 0} onClick={() => onNext(checked)}>Seguir <Icon name="arrow" size={18}></Icon></button>
            </div>
        </div>
    </section>
  )
}

// Paso 4: sumar o reemplazar, y confirmar.
const Confirm = ({ fileName, checked, data, onBack, onDone }) => {
    const { products, categories, business } = data;
    const [mode, setMode] = useState("sumar");
    const [updatePrices, setUpdatePrices] = useState(false);
    const [createCategories, setCreateCategories] = useState(true);
    const [confirmText, setConfirmText] = useState("");
    const [busy, setBusy] = useState(false);

    const options = { mode, updatePrices, createCategories, pro: business.subscribed === true };
    const plan = useMemo(() => planImport(checked, products, categories, options), [checked, products, categories, mode, updatePrices, createCategories]); // eslint-disable-line react-hooks/exhaustive-deps
    const preview = useMemo(() => planImport(checked, products, categories, { ...options, mode: "sumar", createCategories: true }), [checked, products, categories]); // eslint-disable-line react-hooks/exhaustive-deps
    const existingCount = checked.filter((r) => !r.skipped && !r.hasErrors && r.existing).length;
    const replacing = mode === "reemplazar";
    const count = plan.create.length + plan.update.length;
    const ready = (!replacing || confirmText.trim().toUpperCase() === "REEMPLAZAR") && !plan.tooManyCategories && (plan.create.length + plan.update.length + plan.unchanged) > 0;

    const go = async () => {
        setBusy(true);
        if (await data.importCatalog(plan)) onDone(plan);
        else setBusy(false);
    }

  return (
    <div className="lp-imp-layout">
        <section className="lp-panel-card lp-panel-stack lp-imp-gap">
            <h2>¿Qué hacemos con tu catálogo actual?</h2>
            <div className="lp-imp-modes" role="radiogroup" aria-label="Qué hacer con el catálogo actual">
                <button type="button" role="radio" aria-checked={!replacing} className={!replacing ? "lp-imp-mode is-on" : "lp-imp-mode"} onClick={() => setMode("sumar")}>
                    <span className="lp-imp-radio" aria-hidden="true"></span>
                    <strong>Sumar a lo que ya tengo</strong>
                    <span className="lp-panel-muted">Se agregan los productos nuevos. Si uno ya existe con el mismo nombre, le completamos categoría, descripción y etiquetas, pero <b>le dejamos tu precio y tu foto</b>.</span>
                </button>
                <button type="button" role="radio" aria-checked={replacing} className={replacing ? "lp-imp-mode is-danger is-on" : "lp-imp-mode is-danger"} onClick={() => setMode("reemplazar")} disabled={!products.length}>
                    <span className="lp-imp-radio" aria-hidden="true"></span>
                    <strong>Reemplazar todo mi catálogo</strong>
                    <span className="lp-panel-muted">{products.length ? `Se borran tus ${plural(products.length, "producto actual", "productos actuales")} (con sus fotos) y quedan solo los del archivo.` : "Todavía no tenés productos cargados."}</span>
                </button>
            </div>

            {!replacing && existingCount > 0 &&
                <label className="lp-imp-check">
                    <input type="checkbox" checked={updatePrices} onChange={(e) => setUpdatePrices(e.target.checked)}/>
                    <span>También actualizar los precios de {existingCount === 1 ? "el que ya existe" : `los ${existingCount} que ya existen`} <span className="lp-panel-muted">(solo si tu archivo tiene los precios de hoy)</span></span>
                </label>
            }
            {!replacing && preview.addedCategories.length > 0 &&
                <label className="lp-imp-check">
                    <input type="checkbox" checked={createCategories} onChange={(e) => setCreateCategories(e.target.checked)}/>
                    <span>Crear {preview.addedCategories.length === 1 ? "la categoría nueva" : "las categorías nuevas"} <strong>{preview.addedCategories.join(", ")}</strong>
                        {!createCategories && <span className="lp-panel-muted"> (sus productos no se van a importar)</span>}</span>
                </label>
            }

            {replacing ?
                <>
                    <p className="lp-imp-note is-err"><Icon name="alert" size={18} strokeWidth={2.6}></Icon><span><b>Vas a borrar {plural(products.length, "producto", "productos")} y sus fotos.</b> Tus clientes van a ver el menú nuevo apenas confirmes, y esto no se puede deshacer.</span></p>
                    <div className="lp-field">
                        <label htmlFor="imp-confirmar">Escribí <span className="lp-imp-danger-text">REEMPLAZAR</span> para confirmar</label>
                        <input id="imp-confirmar" autoComplete="off" value={confirmText} onChange={(e) => setConfirmText(e.target.value)}/>
                    </div>
                </> :
                <p className="lp-imp-note is-info"><Icon name="refresh" size={18} strokeWidth={2.2}></Icon><span>Si algo no te gusta, durante 24 horas vas a poder <b>deshacer la importación</b> desde Productos.</span></p>
            }
            {plan.tooManyCategories && <p className="lp-imp-note is-err" role="alert"><Icon name="alert" size={18} strokeWidth={2.6}></Icon><span>Quedarían más de {MAX_CATEGORIES} categorías. Revisá la columna Categoría del archivo.</span></p>}

            <div className="lp-imp-footer">
                <button type="button" className="lp-btn lp-btn-secondary" onClick={onBack} disabled={busy}>Atrás</button>
                <button type="button" className={replacing ? "lp-btn lp-panel-btn-danger" : "lp-btn lp-btn-primary"} disabled={!ready || busy} onClick={go}>
                    {busy ? "Importando..." : <><Icon name="up" size={18}></Icon> {replacing ? `Reemplazar por ${plural(plan.create.length, "producto", "productos")}` : count ? `Importar ${plural(count, "producto", "productos")}` : "Importar"}</>}
                </button>
            </div>
        </section>
        <section className="lp-panel-card lp-panel-stack">
            <h2 className="lp-imp-h3">Resumen</h2>
            <dl className="lp-imp-kv">
                <div><dt>Archivo</dt><dd>{fileName}</dd></div>
                <div><dt>Productos nuevos</dt><dd>{plan.create.length}</dd></div>
                {!replacing && <div><dt>{updatePrices ? "Se actualizan" : "Ya existen (se mantiene su precio)"}</dt><dd>{plan.update.length + plan.unchanged}</dd></div>}
                {replacing && <div><dt>Se borran</dt><dd>{plan.remove.length}</dd></div>}
                <div><dt>No se importan</dt><dd>{plan.skipped}</dd></div>
                <div><dt>Categorías nuevas</dt><dd>{plan.addedCategories.length}</dd></div>
                <div><dt>Quedan en tu menú</dt><dd>{plural(plan.total, "producto", "productos")}</dd></div>
            </dl>
        </section>
    </div>
  )
}

const Done = ({ result, data, onClose }) => {
    const noPhoto = result.create.length;
  return (
    <section className="lp-panel-card lp-panel-stack lp-imp-done">
        <span className="lp-imp-done-ic" aria-hidden="true"><Icon name="check" size={38} strokeWidth={2.6}></Icon></span>
        <h2>{result.mode === "reemplazar" ? `Reemplazaste tu catálogo por ${plural(result.create.length, "producto", "productos")}` : `Importaste ${plural(result.create.length + result.update.length, "producto", "productos")}`}</h2>
        <p className="lp-panel-muted">
            {result.mode === "reemplazar" ? "Ya los ven tus clientes en el menú." :
                `${plural(result.create.length, "nuevo", "nuevos")}${result.update.length ? ` y ${plural(result.update.length, "actualizado", "actualizados")}` : ""}. Ya los ven tus clientes en el menú.`}
        </p>
        {noPhoto > 0 && <p className="lp-imp-note is-warn"><Icon name="alert" size={18} strokeWidth={2.4}></Icon><span><b>{noPhoto === 1 ? "1 producto no tiene foto." : `${noPhoto} productos no tienen foto.`}</b> Los productos con foto se piden más: podés ir agregándolas de a poco desde Productos.</span></p>}
        <div className="lp-panel-actions">
            <button type="button" className="lp-btn lp-btn-primary" onClick={onClose}>Ver mis productos</button>
            <a className="lp-btn lp-btn-secondary" href={menuUrl(data.business.usuarioEmpresa)} target="_blank" rel="noreferrer">Ver mi menú <Icon name="external" size={18}></Icon></a>
        </div>
        {result.mode === "sumar" && data.lastImport &&
            <button type="button" className="lp-text-link lp-imp-undo" onClick={async () => { if (await data.undoImport()) onClose(); }}><Icon name="refresh" size={16}></Icon> Deshacer importación</button>
        }
    </section>
  )
}

// Importar el catálogo desde Excel o CSV (plan Pro). Ocupa la pantalla de Productos mientras dura.
const ImportPanel = ({ data, onClose }) => {
    const [step, setStep] = useState(0);
    const [fileName, setFileName] = useState("");
    const [table, setTable] = useState(null);
    const [mapping, setMapping] = useState([]);
    const [skippedColumns, setSkippedColumns] = useState(false);
    const [rows, setRows] = useState([]);
    const [checked, setChecked] = useState([]);
    const [result, setResult] = useState(null);
    const [aiInfo, setAiInfo] = useState(null);

    const top = () => window.scrollTo(0, 0);

    const loaded = (name, t, ai = null) => {
        const auto = detectMapping(t.headers);
        const remembered = ai ? null : savedMapping(t.headers);
        const m = ai ? AI_MAPPING : remembered || auto;
        setFileName(name);
        setTable(t);
        setMapping(m);
        setAiInfo(ai);
        const skip = !!ai || (mappingIsComplete(m) && (!!remembered || looksLikeTemplate(t.headers, auto)));
        setSkippedColumns(skip);
        if (skip) {
            setRows(buildRows(t.rows, m));
            setStep(2);
        } else setStep(1);
        top();
    }

    const subtitle = [
        "Cargá todos tus productos de una vez, sin escribirlos uno por uno.",
        "Tu archivo tiene otras columnas. Decinos qué es cada una.",
        aiInfo ? "Esto es lo que leyó la IA. Revisalo y tocá cualquier dato para corregirlo." : "Así van a quedar en tu menú. Tocá cualquier dato para corregirlo.",
        "Último paso: elegí qué hacemos con lo que ya tenés cargado.",
        "¡Listo! Tu menú ya se actualizó."
    ][step];

  return (
    <div className="lp-panel-page lp-imp">
        {step < 4 && <button type="button" className="lp-imp-back" onClick={onClose}><Icon name="arrow" size={16} strokeWidth={2.4}></Icon> Volver a Productos</button>}
        <div className="lp-panel-head">
            <div>
                <h1>Importar catálogo</h1>
                <p className="lp-panel-muted">{subtitle}</p>
            </div>
        </div>
        <Steps current={step} skipped={skippedColumns && step >= 2}></Steps>

        {step === 0 && <PickFile onLoaded={loaded} initialSource={aiInfo ? "ia" : "excel"}></PickFile>}
        {step === 1 && table &&
            <MapColumns fileName={fileName} table={table} mapping={mapping} setMapping={setMapping}
                onBack={() => setStep(0)}
                onNext={() => {
                    saveMapping(table.headers, mapping);
                    setRows(buildRows(table.rows, mapping));
                    setStep(2);
                    top();
                }}></MapColumns>
        }
        {step === 2 && aiInfo &&
            <p className={"lp-imp-note " + (aiInfo.incomplete ? "is-warn" : "is-info")}>
                <Icon name={aiInfo.incomplete ? "alert" : "star"} size={18} strokeWidth={2.4}></Icon>
                <span>{aiInfo.incomplete
                    ? "La carta era muy larga y la IA no llegó a leerla entera. Revisá que estén todos los productos, o subila en partes."
                    : "La IA puede equivocarse. Fijate sobre todo los precios: los que no pudo leer quedan marcados para que los completes."}</span>
            </p>
        }
        {step === 2 &&
            <Review rows={rows} setRows={setRows} products={data.products} categories={data.categories}
                onBack={() => { setStep(skippedColumns ? 0 : 1); top(); }}
                onNext={(c) => { setChecked(c); setStep(3); top(); }}></Review>
        }
        {step === 3 &&
            <Confirm fileName={fileName} checked={checked} data={data}
                onBack={() => { setStep(2); top(); }}
                onDone={(plan) => { setResult(plan); setStep(4); top(); }}></Confirm>
        }
        {step === 4 && result && <Done result={result} data={data} onClose={onClose}></Done>}
    </div>
  )
}

export default ImportPanel
